'use client';

import { useEffect, useMemo, useState } from 'react';

import {
    Align,
    Button,
    ButtonVariant,
    DirectionEnum,
    Divider,
    FlexBox,
    FlexItem,
    GapSize,
    Icon,
    IconName,
    Link,
    Modal,
    ModalFooter,
    ModalSize,
    RadioTile,
    RadioTiles,
    Text,
    TextLevels,
    Title,
    TitleLevels,
    TitleMarkup,
} from '@bytel/trilogy-react';

import { OptionType, SubscribedOption } from '@app-types/product';

import { useCart } from '@hooks/use-cart';

import { formatPhoneNumber, formatPrice } from '@helpers/utils';

import ParentalControlModal from './parental-control-modal';
import { ParentalRadio } from './parental-radio';

interface ParentalControlProps {
    parentalOptions: OptionType[];
    cartOptions?: OptionType[];
    subscribedOptions?: SubscribedOption[];
}

const noParentalControlOption: OptionType = {
    name: '',
    prices: { initial: 0, forever: 0, final: 0 },
    gencode: '',
    image: '',
    price: 0,
    small_image: '',
    promoLabel: '',
    pennantText: '',
    description: '',
};

export function ParentalControl({
    parentalOptions = [],
    cartOptions,
    subscribedOptions = [],
}: Readonly<ParentalControlProps>) {
    const [isOpen, setIsOpen] = useState(false);
    const { byouCart, updateCartOptions, updateCart } = useCart();
    const [shouldShowParentalControlModal, setShouldShowParentalControlModal] = useState(false);

    useEffect(() => {
        if (byouCart?.isMinor) {
            setIsOpen(true);
        }
    }, [byouCart?.isMinor]);

    const selectedParentalOption = parentalOptions.find((o) =>
        cartOptions?.find((option) => option.gencode === o.gencode),
    );

    useEffect(() => {
        if (isOpen && parentalOptions?.length) {
            if (!selectedParentalOption && sortedParentalOptions[0]) {
                updateCartOptions(sortedParentalOptions[0]);
            }
        }
    }, [isOpen]);

    const sortedParentalOptions = useMemo(
        () => [...(parentalOptions ?? [])].sort((a, b) => (a.prices?.forever ?? 0) - (b.prices?.forever ?? 0)),
        [parentalOptions],
    );

    const parentalOptionCodes = useMemo(
        () => new Set(parentalOptions.map((option) => option.gencode)),
        [parentalOptions],
    );

    const handleChangeOptionPatental = (option: OptionType): void => {
        if (selectedParentalOption) {
            updateCartOptions(selectedParentalOption);
        }
        updateCartOptions(option);
    };

    const formatPromoLabel = (price: number, promoLabel?: string) => {
        let label = `${formatPrice(price)} /mois`;
        if (promoLabel) {
            label += ` - ${promoLabel}`;
        }
        return label;
    };

    const handleMinorBlock = (value: boolean) => () => {
        if (!value && selectedParentalOption) {
            updateCartOptions(selectedParentalOption);
        }
        updateCart({ isMinor: value });
        setIsOpen(value);
    };

    const subscriptionMessages = useMemo(
        () =>
            subscribedOptions
                .filter((s) => s.phoneNumber && parentalOptionCodes.has(s.optionId))
                .map((s) => ({
                    key: s.optionId,
                    message: `Vous bénéficiez déjà de ${s.label} sur la ligne ${formatPhoneNumber(s.phoneNumber!)}.`,
                })),
        [subscribedOptions, parentalOptionCodes],
    );

    return (
        <>
            <FlexBox direction={DirectionEnum.COLUMN} gap={GapSize.FOUR}>
                <FlexBox direction={DirectionEnum.COLUMN} gap={GapSize.TWO}>
                    <Title level={TitleLevels.FIVE} markup={TitleMarkup.H3}>
                        Vous avez un enfant de moins de 18&nbsp;ans&nbsp;?
                    </Title>
                    <Text level={TextLevels.TWO}>Si oui, on a des solutions pour encadrer son activité en ligne.</Text>
                </FlexBox>
                <RadioTiles>
                    <div className='flex-item' style={{ flex: 1 }}>
                        <RadioTile checked={isOpen} horizontal label='Oui' onChange={handleMinorBlock(true)} />
                    </div>
                    <div className='flex-item' style={{ flex: 1 }}>
                        <RadioTile checked={!isOpen} horizontal label='Non' onChange={handleMinorBlock(false)} />
                    </div>
                </RadioTiles>
            </FlexBox>
            {isOpen ? (
                <>
                    <FlexBox direction={DirectionEnum.COLUMN} mobile>
                        <FlexItem>
                            <Title level={TitleLevels.FIVE} markup={TitleMarkup.H3} marginless>
                                Sélectionnez votre contrôle parental (sans engagement)&nbsp;:
                            </Title>
                        </FlexItem>
                        {subscriptionMessages.length > 0 && (
                            <FlexItem>
                                {subscriptionMessages.map(({ key, message }) => (
                                    <FlexBox key={key} gap={GapSize.TWO} align={Align.CENTER}>
                                        <Icon name={IconName.INFOS_CIRCLE} />
                                        <Text level={TextLevels.TWO}>{message}</Text>
                                    </FlexBox>
                                ))}
                            </FlexItem>
                        )}
                        <FlexItem>
                            <Link onClick={() => setShouldShowParentalControlModal(true)}>Comparer les solutions</Link>
                        </FlexItem>
                    </FlexBox>

                    <RadioTiles>
                        {sortedParentalOptions?.map((o) => {
                            const isChecked = !!cartOptions?.find((cartOption) => cartOption.gencode === o.gencode);
                            return (
                                <ParentalRadio
                                    key={o.gencode}
                                    option={{
                                        ...o,
                                        price: o.prices?.final ?? 0,
                                        promoLabel: formatPromoLabel(o.prices?.final ?? 0, o.promoLabel),
                                    }}
                                    checked={isChecked}
                                    onPress={() => handleChangeOptionPatental(o)}
                                />
                            );
                        })}
                        <ParentalRadio
                            key='null'
                            option={noParentalControlOption}
                            onPress={() => {
                                if (selectedParentalOption) {
                                    updateCartOptions(selectedParentalOption);
                                }
                            }}
                            checked={!selectedParentalOption}
                        />
                    </RadioTiles>
                    <Divider />

                    <Modal
                        size={ModalSize.SMALL}
                        title='Quel contrôle parental choisir&nbsp;?'
                        active={shouldShowParentalControlModal}
                        onClose={() => setShouldShowParentalControlModal(false)}
                    >
                        <ParentalControlModal />
                        <ModalFooter>
                            <Button
                                variant={ButtonVariant.PRIMARY}
                                onClick={() => setShouldShowParentalControlModal(false)}
                            >
                                Fermer
                            </Button>
                        </ModalFooter>
                    </Modal>
                </>
            ) : null}
        </>
    );
}
